import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export type AttendanceStatus = 'confirmed' | 'not_attending' | 'pending';

interface LeagueAttendance {
  id: string;
  user_id: string;
  event_id: string;
  week_number: number;
  status: AttendanceStatus;
  created_at: string;
  updated_at: string;
}

interface PartnerInfo {
  partnerId: string | null;
  isDoublesEvent: boolean;
}

export const useLeagueAttendance = (eventId: string) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Single combined query for partner info and attendance
  const { data, isLoading, error } = useQuery({
    queryKey: ['league-attendance-combined', eventId, user?.id],
    queryFn: async (): Promise<{ 
      attendanceRecords: LeagueAttendance[]; 
      partnerInfo: PartnerInfo;
    }> => {
      if (!user?.id || !eventId) {
        return { 
          attendanceRecords: [], 
          partnerInfo: { partnerId: null, isDoublesEvent: false } 
        };
      }

      // First, get the registration to find partner info
      const { data: regData, error: regError } = await supabase
        .from('registrations')
        .select('user_id, partner_user_id')
        .eq('event_id', eventId)
        .eq('status', 'confirmed')
        .or(`user_id.eq.${user.id},partner_user_id.eq.${user.id}`)
        // Puede haber varias inscripciones por evento (una por categoría/subcategoría)
        .limit(1)
        .maybeSingle();

      if (regError) {
        console.error('Error fetching registration:', regError);
      }

      // Determine partner info
      let partnerId: string | null = null;
      let isDoublesEvent = false;

      if (regData) {
        const isPlayer1 = regData.user_id === user.id;
        partnerId = isPlayer1 ? regData.partner_user_id : regData.user_id;
        isDoublesEvent = !!regData.partner_user_id;
      }

      // Now fetch attendance for both users
      const userIds = [user.id];
      if (partnerId) {
        userIds.push(partnerId);
      }

      const { data: attendanceData, error: attendanceError } = await supabase
        .from('league_attendance')
        .select('*')
        .eq('event_id', eventId)
        .in('user_id', userIds);

      if (attendanceError) {
        console.error('Error fetching attendance:', attendanceError);
        throw attendanceError;
      }

      return {
        attendanceRecords: (attendanceData as LeagueAttendance[]) || [],
        partnerInfo: { partnerId, isDoublesEvent },
      };
    },
    enabled: !!user?.id && !!eventId,
  });

  const attendanceRecords = data?.attendanceRecords || [];
  const partnerInfo = data?.partnerInfo || { partnerId: null, isDoublesEvent: false };

  // Convert array to Record for easy lookup
  const attendance: Record<number, AttendanceStatus> = {};
  attendanceRecords.forEach((record) => {
    const weekNum = record.week_number;
    if (!attendance[weekNum]) {
      attendance[weekNum] = record.status;
    }
  });

  // Update attendance mutation
  const updateAttendanceMutation = useMutation({
    mutationFn: async ({ weekNumber, status }: { weekNumber: number; status: AttendanceStatus }) => {
      if (!user?.id || !eventId) {
        throw new Error('Usuario no autenticado');
      }

      const records = [
        {
          user_id: user.id,
          event_id: eventId,
          week_number: weekNumber,
          status: status,
        },
      ];

      if (partnerInfo.partnerId) {
        records.push({
          user_id: partnerInfo.partnerId,
          event_id: eventId,
          week_number: weekNumber,
          status: status,
        });
      }

      const { data, error } = await supabase
        .from('league_attendance')
        .upsert(records, {
          onConflict: 'user_id,event_id,week_number',
        })
        .select();

      if (error) {
        console.error('Error updating attendance:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['league-attendance-combined', eventId] });
      
      const isDoubles = partnerInfo.isDoublesEvent;
      const statusMessage = variables.status === 'confirmed' 
        ? isDoubles 
          ? '¡Asistencia confirmada para ti y tu pareja!' 
          : '¡Asistencia confirmada!'
        : isDoubles
          ? 'Has indicado que no asistirán'
          : 'Has indicado que no asistirás';
      
      toast.success(statusMessage);
    },
    onError: (error) => {
      console.error('Error updating attendance:', error);
      toast.error('Error al actualizar asistencia');
    },
  });

  const updateAttendance = (weekNumber: number, uiStatus: 'attending' | 'not-attending') => {
    const dbStatus: AttendanceStatus = uiStatus === 'attending' ? 'confirmed' : 'not_attending';
    updateAttendanceMutation.mutate({ weekNumber, status: dbStatus });
  };

  const getUIAttendance = (): Record<number, 'attending' | 'not-attending' | null> => {
    const uiAttendance: Record<number, 'attending' | 'not-attending' | null> = {};
    Object.entries(attendance).forEach(([weekNum, status]) => {
      if (status === 'confirmed') {
        uiAttendance[Number(weekNum)] = 'attending';
      } else if (status === 'not_attending') {
        uiAttendance[Number(weekNum)] = 'not-attending';
      } else {
        uiAttendance[Number(weekNum)] = null;
      }
    });
    return uiAttendance;
  };

  return {
    attendance,
    uiAttendance: getUIAttendance(),
    isLoading,
    error,
    updateAttendance,
    isUpdating: updateAttendanceMutation.isPending,
    isDoublesEvent: partnerInfo.isDoublesEvent,
  };
};
